
import React, { useRef, useEffect, useState } from 'react';

interface SparklesProps {
  id?: string;
  background?: string;
  minSize?: number;
  maxSize?: number;
  particleDensity?: number;
  className?: string;
  particleColor?: string;
  speed?: number;
}

export const SparklesCore = (props: SparklesProps) => {
  const {
    id = "sparkles",
    background = "transparent",
    minSize = 0.4,
    maxSize = 1,
    particleDensity = 120,
    className,
    particleColor = "#FFFFFF",
  } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const particles = useRef<any[]>([]);
  const animationFrameId = useRef<number | null>(null);

  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      setContext(ctx);
      resizeCanvas();
      window.addEventListener("resize", resizeCanvas);
    }
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  const resizeCanvas = () => {
    if (canvasRef.current) {
      const parent = canvasRef.current.parentElement;
      if (parent) {
        canvasRef.current.width = parent.clientWidth;
        canvasRef.current.height = parent.clientHeight;
        initParticles();
      }
    }
  };

  const initParticles = () => {
    if (!canvasRef.current) return;
    const { width, height } = canvasRef.current;
    particles.current = [];
    const count = (width * height) / (1000000 / particleDensity);
    
    for (let i = 0; i < count; i++) {
      particles.current.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * (maxSize - minSize) + minSize,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        opacity: Math.random(),
      });
    }
  };

  const draw = () => {
    if (!context || !canvasRef.current) return;
    const { width, height } = canvasRef.current;
    context.clearRect(0, 0, width, height);

    particles.current.forEach((p) => {
      context.beginPath();
      context.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      context.fillStyle = particleColor;
      context.globalAlpha = p.opacity;
      context.fill();

      p.x += p.speedX;
      p.y += p.speedY;

      if (p.x < 0) p.x = width;
      if (p.x > width) p.x = 0;
      if (p.y < 0) p.y = height;
      if (p.y > height) p.y = 0;
    });

    animationFrameId.current = requestAnimationFrame(draw);
  };

  useEffect(() => {
    if (context) draw();
  }, [context]);

  return (
    <canvas
      ref={canvasRef}
      id={id}
      style={{ background }}
      className={className}
    />
  );
};
